import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { handleApiError, handleApiSuccess } from "./apiErrorHandler";
import axiosInstance from "../services/axiosInstance";

const MoveOrderDialog = ({ visible, onHide, onSaved }) => {
    const [departments, setDepartments] = useState([]);
    const [selectedSource, setSelectedSource] = useState(null);
    const [selectedDestination, setSelectedDestination] = useState(null);
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [qty, setQty] = useState(null);
    const [batchSplit, setBatchSplit] = useState([]);
    const [transferItems, setTransferItems] = useState([]);
    const [loadingBatches, setLoadingBatches] = useState(false);
    const [requireTransferApproval, setrequireTransferApproval] = useState(false);
    // Fetch departments on mount
    useEffect(() => {
        if (visible){
            axiosInstance.get("api/departments/").then((res) => setDepartments(res.data));
            const s = JSON.parse(localStorage.getItem('organizationSettings') || '{}');
            if (s.require_transfer_move_order_approval) {
                setrequireTransferApproval(true);
            } else {
                setrequireTransferApproval(false);
            }
        }
    }, [visible]);

    // Fetch products when source dept changes
    useEffect(() => {
        if (selectedSource) {
            axiosInstance
                .get(`api/inventory/?department=${selectedSource.id}&all=1`)
                .then((res) => setProducts(res.data));
        } else {
            setProducts([]);
        }
        setSelectedProduct(null);
        setQty(null);
        setBatchSplit([]);
    }, [selectedSource]);

    // When qty, product, or source dept changes, fetch batch allocation
    useEffect(() => {
        const fetchBatchSplit = async () => {
            if (!selectedProduct || !qty || !selectedSource) {
                setBatchSplit([]);
                return;
            }
            setLoadingBatches(true);
            try {
                const res = await axiosInstance.post("/api/inventory/allocate-batches/", {
                    product_id: selectedProduct.product,
                    department_id: selectedSource.id,
                    quantity: qty,
                });
                setBatchSplit(res.data.allocations || []);
            } catch (err) {
                setBatchSplit([]);
                handleApiError(err);
            }
            setLoadingBatches(false);
        };
        fetchBatchSplit();
        // eslint-disable-next-line
    }, [selectedProduct, qty, selectedSource]);

    const productOptions = useMemo(() => {
    const seen = new Set();
    return products
        .filter(p => {
        if (seen.has(p.product)) return false;
        seen.add(p.product);
        return true;
        })
        .map(p => ({
        product_label: `${p.product_name} (${p.quantity})`,
        value: p  
        }));
    }, [products]);

    // Add batch split to transfer items table
    const addTransferItem = () => {
        if (!selectedProduct || !qty || !batchSplit.length || !selectedDestination) {
            handleApiError({Error: "Select all fields and batch allocation first!"});
            return;
        }
        // Prevent duplicate entry for same product in this session
        if (
            transferItems.some(
                (i) => i.product === selectedProduct.product && i.destination_department === selectedDestination.id
            )
        ) {
            handleApiError({Warning: "Product already added to transfer list for this destination."});
            return;
        }
        // Add each batch allocation as a line
        setTransferItems((prev) => [
            ...prev,
            ...batchSplit.map((batch) => ({
                product: selectedProduct.product,
                product_name: selectedProduct.product_name,
                quantity: batch.allocated_quantity,
                batch_number: batch.batch_number,
                expiry_date: batch.expiry_date,
                selling_price: batch.selling_price,
                purchase_price: batch.purchase_price,
                source_department: selectedSource.id,
                destination_department: selectedDestination.id,
                price_list: batch.price_list_pk,
                supplier: batch.supplier,
            })),
        ]);
        // Reset
        setSelectedProduct(null);
        setQty(null);
        setBatchSplit([]);
    };

    // Remove a transfer item
    const removeTransferItem = (rowIdx) => {
        setTransferItems((prev) => prev.filter((_, idx) => idx !== rowIdx));
    };
    
    // Handle the transfer submission
    const handleSubmit = async () => {
        if (!selectedSource || !selectedDestination || transferItems.length === 0) {
            handleApiError({Error: "All fields and at least one product are required!"});
            return;
        }
        // Group lines by product and batch for payload
        const moveOrderData = {
            is_purchase: false,
            is_delete: false,
            source_department: selectedSource.id,
            destination_department: selectedDestination.id,
            items: transferItems.map((item) => ({
                product: item.product,
                quantity: item.quantity,
                batch_number: item.batch_number,
                expiry_date: item.expiry_date,
                selling_price: item.selling_price,
                purchase_price: item.purchase_price,
                price_list: item.price_list,
                supplier: item.supplier,
            })),
        };
        try {
            const createRes = await axiosInstance.post(
                "/api/inventory/move-order/",
                moveOrderData
            );
            const moveOrderId = createRes.data.id;
            if (requireTransferApproval) {
                const res = await axiosInstance.post(`/api/inventory/move-order/${moveOrderId}/send_for_approval/`);
                handleApiSuccess(res.data?.message || 'Sent for Approval');
            } else {
            await axiosInstance.post(`/api/inventory/move-order/${moveOrderId}/dispatch-goods/`);
            handleApiSuccess("Inventory Goods Dispatched!");
            }
            onSaved?.();
            onHide();
        } catch (err) {
            handleApiError(err);
        }
    };

    return (
        <Dialog 
            header={
                <div>
                <span>Transfer Inventory</span>
                <div className="flex flex-wrap align-items-center gap-2 sm:gap-2">
                    <Button
                        label={requireTransferApproval ? "Send for Approval" : "Dispatch Goods"}
                        icon={requireTransferApproval ? "pi pi-send" : "pi pi-check"}
                        className={requireTransferApproval ? "p-button-warning mt-3" : "p-button-success mt-3"}
                        onClick={handleSubmit}
                        style={{ borderRadius: "6px", fontSize: "14px" }}
                    />
                    <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="p-button-secondary mt-3" outlined style={{ borderRadius: '6px', fontSize: '14px' }} />
                </div>
            </div>
            }
            visible={visible} style={{ width: "80vw" }} onHide={onHide} modal draggable={false} closable={false} maximized={true} breakpoints={{ '960px': '75vw', '641px': '90vw' }} >
            <div className="mb-3 flex gap-4">
                <Dropdown
                    value={selectedSource}
                    options={departments}
                    onChange={(e) => setSelectedSource(e.value)}
                    optionLabel="department"
                    placeholder="Source Department"
                    style={{ width: "250px" }}
                />
                <Dropdown
                    value={selectedDestination}
                    options={departments.filter((d) => d.id !== (selectedSource && selectedSource.id))}
                    onChange={(e) => setSelectedDestination(e.value)}
                    optionLabel="department"
                    placeholder="Destination Department"
                    style={{ width: "250px" }}
                />
            </div>
            <div className="mb-3 flex gap-4 align-items-end">
                <Dropdown
                    value={selectedProduct}
                    options={productOptions}
                    onChange={(e) => setSelectedProduct(e.value)}
                    optionLabel="product_label"
                    placeholder="Select Product (by batch)"
                    style={{ width: "250px" }}
                    filter
                    filterBy="product_label"
                />
                <InputNumber
                    value={qty}
                    onValueChange={(e) => setQty(e.value)}
                    placeholder="Quantity to transfer"
                    min={1}
                    style={{ width: "150px" }}
                />
                <Button
                    label="Allocate Batches"
                    disabled={!selectedProduct || !qty || !selectedSource}
                    onClick={addTransferItem}
                    style={{ borderRadius: "6px", fontSize: "14px" }}
                    outlined
                />
            </div>
            {/* Batch Allocation Preview Table */}
            <div className="mb-4">
                {batchSplit.length > 0 && (
                    <>
                        <h5>Batch Split Preview:</h5>
                        <DataTable value={batchSplit} loading={loadingBatches} responsiveLayout="stack">
                            <Column field="batch_number" header="Batch Number" />
                            <Column field="expiry_date" header="Expiry Date" />
                            <Column field="available_quantity" header="Available" />
                            <Column field="allocated_quantity" header="Allocated" />
                            <Column field="selling_price" header="Selling Price" />
                        </DataTable>
                    </>
                )}
            </div>
            {/* Transfer Item Table */}
            <div>
                <h5>Transfer List</h5>
                <DataTable value={transferItems} responsiveLayout="stack">
                    <Column field="product_name" header="Product" />
                    <Column field="batch_number" header="Batch" />
                    <Column field="expiry_date" header="Expiry" />
                    <Column field="quantity" header="Quantity" />
                    <Column field="selling_price" header="Selling Price" />
                    <Column
                        body={(_, { rowIndex }) => (
                            <Button
                                icon="pi pi-trash"
                                className="p-button-danger p-button-text"
                                onClick={() => removeTransferItem(rowIndex)}
                            />
                        )}
                        style={{ width: "4rem" }}
                    />
                </DataTable>
            </div>
        </Dialog>
    );
};

export default MoveOrderDialog;